import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Avatar,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Language as LanguageIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Web as WebIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Article as ArticleIcon,
  Edit as DraftIcon,
  Publish as PublishIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { getPages, getSiteSettings, CMSPage, CMSSiteSettings } from '../../../../firebase/services/cmsService';

interface CMSStats {
  totalPages: number;
  publishedPages: number;
  draftPages: number;
  archivedPages: number;
}

const CMSDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<CMSStats>({
    totalPages: 0,
    publishedPages: 0,
    draftPages: 0,
    archivedPages: 0,
  });
  const [recentPages, setRecentPages] = useState<CMSPage[]>([]);
  const [siteSettings, setSiteSettings] = useState<CMSSiteSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load recent pages and calculate stats
      const [publishedResult, draftResult, archivedResult] = await Promise.all([
        getPages('published', 5),
        getPages('draft', 5),
        getPages('archived', 5),
      ]);

      const allPages = [
        ...publishedResult.pages,
        ...draftResult.pages,
        ...archivedResult.pages,
      ];

      setStats({
        totalPages: allPages.length,
        publishedPages: publishedResult.pages.length,
        draftPages: draftResult.pages.length,
        archivedPages: archivedResult.pages.length,
      });

      // Sort by most recent
      const sortedPages = allPages.sort((a, b) => b.updatedAt - a.updatedAt);
      setRecentPages(sortedPages.slice(0, 5));

      // Load site settings
      const settings = await getSiteSettings();
      setSiteSettings(settings);

    } catch (err: any) {
      console.error('Error loading CMS dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published':
        return <PublishIcon fontSize="small" />;
      case 'draft':
        return <DraftIcon fontSize="small" />;
      case 'archived':
        return <ArchiveIcon fontSize="small" />;
      default:
        return <ArticleIcon fontSize="small" />;
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            <WebIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Website Content Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your website content, pages, navigation, and site settings
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Quick Actions */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<AddIcon />}
                component={Link}
                to="/admin/business/online/pages/new"
                sx={{ py: 2 }}
              >
                Create New Page
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<MenuIcon />}
                component={Link}
                to="/admin/business/online/navigation"
                sx={{ py: 2 }}
              >
                Edit Navigation
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<WebIcon />}
                component={Link}
                to="/admin/business/online/templates"
                sx={{ py: 2 }}
              >
                Page Templates
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<SettingsIcon />}
                component={Link}
                to="/admin/business/online/site-settings"
                sx={{ py: 2 }}
              >
                Site Settings
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<VisibilityIcon />}
                href="/"
                target="_blank"
                sx={{ py: 2 }}
              >
                View Website
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Pages
                    </Typography>
                    <Typography variant="h4">
                      {stats.totalPages}
                    </Typography>
                  </Box>
                  <Avatar sx={{ backgroundColor: 'primary.main' }}>
                    <ArticleIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Published
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.publishedPages}
                    </Typography>
                  </Box>
                  <Avatar sx={{ backgroundColor: 'success.main' }}>
                    <PublishIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Drafts
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {stats.draftPages}
                    </Typography>
                  </Box>
                  <Avatar sx={{ backgroundColor: 'warning.main' }}>
                    <DraftIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Archived
                    </Typography>
                    <Typography variant="h4">
                      {stats.archivedPages}
                    </Typography>
                  </Box>
                  <Avatar sx={{ backgroundColor: 'grey.500' }}>
                    <ArchiveIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          {/* Recent Pages */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Recent Pages
                  </Typography>
                  <Button 
                    component={Link} 
                    to="/admin/business/online/pages"
                    endIcon={<EditIcon />}
                  >
                    View All
                  </Button>
                </Box>
                
                {recentPages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">
                      No pages created yet
                    </Typography>
                    <Button
                      variant="contained"
                      component={Link}
                      to="/admin/business/online/pages/new"
                      sx={{ mt: 2 }}
                      startIcon={<AddIcon />}
                    >
                      Create Your First Page
                    </Button>
                  </Box>
                ) : (
                  <List>
                    {recentPages.map((page, index) => (
                      <React.Fragment key={page.id}>
                        <ListItem
                          sx={{ px: 0 }}
                          secondaryAction={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={page.status}
                                color={getStatusColor(page.status) as any}
                                size="small"
                                icon={getStatusIcon(page.status)}
                              />
                              <IconButton
                                component={Link}
                                to={`/admin/business/online/pages/${page.id}/edit`}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <ArticleIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={page.title}
                            secondary={`Updated ${formatDate(page.updatedAt)} • /${page.slug}`}
                          />
                        </ListItem>
                        {index < recentPages.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Site Overview */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Site Overview
                </Typography>
                
                {siteSettings ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Site Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {siteSettings.siteName || 'Fallen Leaf Lodge'}
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
                      Description
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {siteSettings.siteDescription || 'Your gateway to adventure on Fallen Leaf Lake'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ mb: 3 }}>
                    <Typography color="textSecondary">
                      Site settings not configured
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      component={Link}
                      to="/admin/business/online/site-settings"
                      sx={{ mt: 1 }}
                    >
                      Configure Now
                    </Button>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Content Health
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Published Content</Typography>
                    <Typography variant="body2">
                      {stats.totalPages > 0 ? Math.round((stats.publishedPages / stats.totalPages) * 100) : 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.totalPages > 0 ? (stats.publishedPages / stats.totalPages) * 100 : 0}
                    color={stats.publishedPages > stats.draftPages ? 'success' : 'warning'}
                  />
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  component={Link}
                  to="/admin/business/online/pages"
                  sx={{ mt: 2 }}
                >
                  Manage All Pages
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
  );
};

export default CMSDashboard; 